import type { AlertTon } from "./alert";

/**
 * Farbiger Kopf einer Zustands-Karte (W204 B, Betreiber 15.10.2026): Symbol im Kreis, Titel und der Satz zum Zustand.
 * Er sitzt als Slot `kopf` oben in der `Handlungskarte` und ersetzt dort den früheren eigenen Zustandsträger-Alert:
 * Zustand und „Deine nächste Handlung“ stehen in EINER Karte. Töne wie der `Alert` (Titel neutral `text-foreground`, Farbe nur in Kreis und Fläche, W206) (Fehler/Warnung rot/gelb als Ablehnungs- bzw.
 * Warnfläche, Erfolg teal, Info primär), je Ton feste, vollständige Klassenketten. Rollen wie beim `Alert`: Fehler und Warnung
 * `role="alert"`, sonst `role="status"`. Der Baustein kennt keine Fachregeln; Titel, Satz, Links und Liste kommen vom Aufrufer.
 */
const TON: Record<Exclude<AlertTon, "neutral">, { kopf: string; kreis: string; titel: string }> = {
  fehler: { kopf: "bg-red-50 p-4 flex gap-x-3 border-b border-line-2", kreis: "shrink-0 inline-flex justify-center items-center size-8 rounded-full border-4 border-red-100 bg-red-200 text-red-800", titel: "block font-semibold text-foreground" },
  warnung: { kopf: "bg-yellow-50 p-4 flex gap-x-3 border-b border-line-2", kreis: "shrink-0 inline-flex justify-center items-center size-8 rounded-full border-4 border-yellow-100 bg-yellow-200 text-yellow-800", titel: "block font-semibold text-foreground" },
  info: { kopf: "bg-primary-50 p-4 flex gap-x-3 border-b border-line-2", kreis: "shrink-0 inline-flex justify-center items-center size-8 rounded-full border-4 border-primary-100 bg-primary-200 text-primary-800", titel: "block font-semibold text-foreground" },
  erfolg: { kopf: "bg-teal-50 p-4 flex gap-x-3 border-b border-line-2", kreis: "shrink-0 inline-flex justify-center items-center size-8 rounded-full border-4 border-teal-100 bg-teal-200 text-teal-800", titel: "block font-semibold text-foreground" },
};

export function ZustandsKopf({
  ton,
  titel,
  symbol,
  children,
  id,
}: {
  ton: Exclude<AlertTon, "neutral">;
  titel: string;
  /** Lucide-Symbol (Aufrufer setzt `shrink-0 size-4`, `aria-hidden`). */
  symbol: React.ReactNode;
  /** Der Satz zum Zustand, ggf. mit Links und Liste (Kinder, ohne eigene Klassen). */
  children?: React.ReactNode;
  /** Eindeutige Id für aria-labelledby. */
  id: string;
}) {
  const t = TON[ton];
  const role = ton === "fehler" || ton === "warnung" ? "alert" : "status";
  const labelId = `${id}-label`;
  return (
    <>
      {/* Preline: component/alerts/alerts-bordered-styles (Symbol im Kreis border-4, Fläche -50, Titel text-foreground font-semibold, Beschreibung) */}
      <div id={id} className={t.kopf} role={role} tabIndex={-1} aria-labelledby={labelId}>
        <span className={t.kreis} aria-hidden="true">
          {symbol}
        </span>
        <div className="min-w-0 text-sm text-foreground">
          <span id={labelId} className={t.titel}>
            {titel}
          </span>
          {children ? " " : null}
          {children ? <div className="mt-1">{children}</div> : null}
        </div>
      </div>
    </>
  );
}
